import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageHeader from 'components/PageHeader';
import buildRoute from 'components/buildRoute';
import TableContainerWithScrollShadow from 'components/TableContainerWithScrollShadow';
import { deleteExperimentFile, fetchList, getFileDownloadUrl, postAction } from './registryApi';
import {
  CONTACT_MODE,
  DOCUMENT_CATEGORY,
  ELECTION_GEO_TARGET,
  ELECTION_TYPE,
  EMBARGO_PERIOD,
  EXPERIMENT_STATUS,
  FILE_KIND,
  FUNDING_STATUS,
  OFFICE_TOP_BALLOT,
  OUTCOME,
  OUTCOME_DATA_SOURCE,
  POLITICAL_ORG_TYPE_527,
  POST_EMBARGO_ACCESS,
  PROGRAM_GOAL,
  REFERENCE_GROUP,
  REGRESSION_MODEL,
} from './registryChoices';
import {
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  labelFor,
  labelsFor,
  orDash,
  stateNames,
} from './registryFormat';
import RegistryBreadcrumbs from './components/RegistryBreadcrumbs';
import RegistryStatusChip from './components/RegistryStatusChip';
import useExperimentFileUpload from './wizard/useExperimentFileUpload';

const join = (...parts) => parts.filter(Boolean).join(' · ');
// Control is position 0, so it leads the Verify Counts table (wireframe 3b).
const sortedArms = (treatments) => [...treatments].sort((a, b) => a.position - b.position);
const armName = (arm) => (arm.is_control ? 'Control — No Contact' : arm.treatment_label || `Treatment ${arm.position}`);

const SectionHeading = ({ children }) => (
  <Typography variant="h4" sx={{ mb: 1.25 }}>
    {children}
  </Typography>
);

/** Label/value rows for a section of the registered record (wireframe 3c). */
const DetailTable = ({ rows }) => (
  <TableContainerWithScrollShadow>
    <Table size="small">
      <TableBody>
        {rows.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell sx={{ width: 220, color: 'text.secondary', verticalAlign: 'top' }}>{label}</TableCell>
            <TableCell sx={{ color: 'text.dark', whiteSpace: 'pre-wrap' }}>{orDash(value)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainerWithScrollShadow>
);

const MatchCountsTable = ({ arms }) => (
  <TableContainerWithScrollShadow>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Condition</TableCell>
          <TableCell align="right">Uploaded N</TableCell>
          <TableCell align="right">Valid IDs</TableCell>
          <TableCell align="right">Match rate</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {arms.map((arm) => (
          <TableRow key={arm.id}>
            <TableCell>{armName(arm)}</TableCell>
            <TableCell align="right">{orDash(formatNumber(arm.match_count?.uploaded_n))}</TableCell>
            <TableCell align="right">{orDash(formatNumber(arm.match_count?.valid_ids))}</TableCell>
            <TableCell align="right">{orDash(formatPercent(arm.match_count?.match_rate))}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainerWithScrollShadow>
);

const vendorName = (experiment) =>
  experiment.outcome_data_source === 'other'
    ? experiment.outcome_data_source_other || 'your outcome data source'
    : labelFor(OUTCOME_DATA_SOURCE, experiment.outcome_data_source) || 'your outcome data source';

const ValidatingView = ({ experiment }) =>
  // Only Catalist and TargetSmart voter files are matched; a write-in source isn't validated.
  experiment.outcome_data_source === 'other' ? (
    <Alert severity="info" square>
      <AlertTitle>Registration submitted</AlertTitle>
      Automatic ID validation is currently only available for Catalist and TargetSmart, so your uploaded IDs
      will not be matched to a voter file. We&apos;re currently processing your submission — please return in an
      hour to confirm your raw counts to finalize the registration.
    </Alert>
  ) : (
    <Alert severity="info" square>
      <AlertTitle>Validating your voter IDs</AlertTitle>
      We&apos;re validating your uploaded IDs against {vendorName(experiment)}. This usually takes about an hour.
      When it&apos;s done, you&apos;ll review your match summary here and confirm your counts to finalize the
      registration.
    </Alert>
  );

const VerifyCountsView = ({ experiment, onChanged }) => {
  const inputRef = useRef(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const { uploadFile, uploading, error: uploadError } = useExperimentFileUpload({
    experimentId: experiment.id,
    kind: FILE_KIND.DATA,
  });

  const handleConfirm = async () => {
    setConfirming(true);
    setError('');
    try {
      onChanged(await postAction('/registry/my-experiments', experiment.id, 'confirm_counts'));
    } catch (e) {
      setError('Could not confirm your counts. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const handleReupload = async (file) => {
    if (!file) return;
    setError('');
    const previous = experiment.files.filter((f) => f.kind === FILE_KIND.DATA);
    const uploaded = await uploadFile(file).catch(() => null);
    if (!uploaded) return;
    await Promise.all(previous.map((f) => deleteExperimentFile(f.id).catch(() => null)));
    onChanged();
  };

  return (
    <>
      <Alert severity="warning" square sx={{ mb: 3 }}>
        <AlertTitle>Review your match summary</AlertTitle>
        {experiment.outcome_data_source === 'other' ? (
          // Write-in sources aren't matched against a voter file, so there are no valid IDs.
          <>
            Check the counts below against your randomization records, then confirm to finalize your registration —
            or re-upload your data file if something looks wrong. Note that you will see 0 valid IDs, because your
            data was not matched against a voterfile - that&apos;s expected. If you see no uploaded N, double-check that
            your universe file correctly labeled your treatments as numeric values (0 for control, 1 for the first
            treatment entered in the form, 2 for the second treatment, etc).
          </>
        ) : (
          <>
            We validated your voter IDs against {vendorName(experiment)}. Check the counts below against your
            randomization records, then confirm to finalize your registration — or re-upload your data file if
            something looks wrong. If you see no uploaded N, double-check that your universe file correctly labeled
            your treatments as numeric values (0 for control, 1 for the first treatment entered in the form, 2 for the
            second treatment, etc).
          </>
        )}
      </Alert>

      {(error || uploadError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || uploadError}
        </Alert>
      )}

      <SectionHeading>Per-arm counts &amp; match rates</SectionHeading>
      <Box sx={{ mb: 3 }}>
        <MatchCountsTable arms={sortedArms(experiment.treatments)} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.25 }}>
        <Button variant="contained" onClick={handleConfirm} disabled={confirming || uploading}>
          Confirm &amp; Finalize Registration
        </Button>
        <Button
          variant="outlined"
          onClick={() => inputRef.current?.click()}
          disabled={confirming || uploading}
          startIcon={uploading ? <CircularProgress size={16} /> : null}
        >
          {uploading ? 'Uploading…' : 'Re-upload Data File'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          hidden
          onChange={(e) => {
            handleReupload(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </Box>
    </>
  );
};

const NeedsApprovalView = ({ experiment, onChanged }) => {
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState('');
  const isAdmin = experiment.viewer_is_admin;

  const handleApprove = async () => {
    setApproving(true);
    setError('');
    try {
      onChanged(await postAction('/registry/my-experiments', experiment.id, 'approve'));
    } catch (e) {
      setError('Could not approve this registration. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    setRejecting(true);
    setError('');
    try {
      onChanged(await postAction('/registry/my-experiments', experiment.id, 'reject'));
    } catch (e) {
      setError('Could not send this registration back. Please try again.');
    } finally {
      setRejecting(false);
    }
  };

  return (
    <>
      <Alert severity="info" square sx={{ mb: error ? 2 : isAdmin ? 2 : 3 }}>
        <AlertTitle>Pending Analyst Institute review</AlertTitle>
        Your registration has been submitted for Analyst Institute approval. You&apos;ll be notified
        once it&apos;s reviewed.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: isAdmin ? 2 : 3 }}>
          {error}
        </Alert>
      )}
      {isAdmin && (
        <Box sx={{ display: 'flex', gap: 1.25, mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={approving || rejecting}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleReject}
            disabled={approving || rejecting}
          >
            Send Back for Updates
          </Button>
        </Box>
      )}
    </>
  );
};

const fileTypeLabel = (file, treatments) => {
  if (file.kind === FILE_KIND.DATA) return 'Experiment data (validated)';
  if (file.kind === FILE_KIND.CREATIVE) {
    const arm = treatments.find((t) => t.id === file.treatment);
    return `Creative — ${arm ? armName(arm) : 'treatment'}`;
  }
  return labelFor(DOCUMENT_CATEGORY, file.document_category) || 'Additional document';
};

const RegisteredView = ({ experiment }) => {
  const [downloadError, setDownloadError] = useState('');
  const arms = [
    ...sortedArms(experiment.treatments).filter((t) => !t.is_control),
    ...experiment.treatments.filter((t) => t.is_control),
  ];
  const outcome = experiment.outcomes[0] || {};
  const isShared = experiment.post_embargo_access !== 'none';
  // False when viewing another org's experiment that was shared with you: the API omits
  // match counts, files, and the confirmer, so those parts of the record aren't shown.
  const isOwner = experiment.viewer_is_owner;

  const handleDownload = async (fileId) => {
    setDownloadError('');
    try {
      const { download_url: url } = await getFileDownloadUrl(fileId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setDownloadError('Could not get a download link for that file.');
    }
  };

  const funding =
    experiment.funding_status === '527' && experiment.political_org_type_527
      ? `${labelFor(FUNDING_STATUS, experiment.funding_status)} — ${labelFor(
          POLITICAL_ORG_TYPE_527,
          experiment.political_org_type_527
        )}`
      : labelFor(FUNDING_STATUS, experiment.funding_status);

  const geoTarget =
    experiment.election_geo_target === 'other'
      ? experiment.election_geo_target_other
      : labelFor(ELECTION_GEO_TARGET, experiment.election_geo_target);

  const programDates =
    experiment.program_start_timing || experiment.program_end_timing
      ? `${formatDate(experiment.program_start_timing) || '—'} – ${formatDate(experiment.program_end_timing) || '—'}`
      : '';

  const regressionModel =
    outcome.regression_model === 'other'
      ? outcome.regression_model_other
      : // The BigQuery read folds an "Other" write-in into regression_model itself.
        labelFor(REGRESSION_MODEL, outcome.regression_model) || outcome.regression_model;

  return (
    <>
      {/* Also rendered while pending approval (as the read-only record), where this would be wrong. */}
      {isOwner && experiment.status === EXPERIMENT_STATUS.REGISTERED && (
        <Alert severity="success" square sx={{ mb: 3 }}>
          <AlertTitle>Registration approved</AlertTitle>
          {experiment.counts_confirmed_by_email
            ? `Counts confirmed by ${experiment.counts_confirmed_by_email}. `
            : ''}
          {experiment.registered_at ? `Approved ${formatDateTime(experiment.registered_at)}. ` : ''}
          Your record is locked; contact the Analyst Institute if anything needs to be corrected.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <SectionHeading>General information</SectionHeading>
          <DetailTable
            rows={[
              ['Experiment name', experiment.experiment_name],
              [
                'Partner organizations',
                // Hidden from other orgs on anonymous experiments (the API sends none).
                experiment.anonymous && !experiment.viewer_is_owner
                  ? 'Anonymous'
                  : experiment.partners.join(', '),
              ],
              [
                'Organization',
                experiment.registering_organization_name || (experiment.anonymous ? 'Anonymous' : ''),
              ],
              ['Experiment ID', experiment.registry_id],
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionHeading>Electoral context and program goals</SectionHeading>
          <DetailTable
            rows={[
              [
                'Election type',
                join(labelFor(ELECTION_TYPE, experiment.election_type), formatDate(experiment.election_date)),
              ],
              ['Program year(s)', experiment.year.join(', ')],
              ['Top of ballot', labelsFor(OFFICE_TOP_BALLOT, experiment.office_top_ballot)],
              [
                'Program goal / outcomes',
                join(
                  labelFor(PROGRAM_GOAL, experiment.program_goal),
                  labelsFor(OUTCOME, experiment.outcomes.map((o) => o.outcome_name))
                ),
              ],
              ['States / geographic unit', join(stateNames(experiment.states), geoTarget)],
              ['Funding source', funding],
            ]}
          />
        </Grid>
      </Grid>

      <SectionHeading>The experiment</SectionHeading>
      <Box sx={{ mb: 3 }}>
        <DetailTable
          rows={[
            ['Description', experiment.experiment_description],
            ['Research questions', experiment.research_questions.join('\n')],
            ['Targeting criteria', experiment.targeting_criteria],
            ['Randomization', experiment.randomization_description],
            [
              'Clustered',
              experiment.clustered === null ? '' : experiment.clustered ? `Yes — ${experiment.cluster_level}` : 'No',
            ],
            ['Stopping rules', experiment.stopping_rules],
            [
              'Treatments / reference group',
              join(
                experiment.num_treatments
                  ? `${experiment.num_treatments} treatment${experiment.num_treatments === 1 ? '' : 's'}`
                  : '',
                labelsFor(REFERENCE_GROUP, experiment.reference_group)
              ),
            ],
            ['Outcome data source', vendorName(experiment)],
            ['Program dates', programDates],
            ['Total units', formatNumber(experiment.total_n)],
          ]}
        />
      </Box>

      <SectionHeading>The treatments</SectionHeading>
      <Box sx={{ mb: 3 }}>
        <TableContainerWithScrollShadow>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Condition</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Modes</TableCell>
                {isOwner ? (
                  <>
                    <TableCell align="right">Uploaded N</TableCell>
                    <TableCell align="right">Valid IDs</TableCell>
                    <TableCell align="right">Match rate</TableCell>
                  </>
                ) : (
                  <TableCell align="right">Units</TableCell>
                )}
                <TableCell>Dates</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {arms.map((arm) => (
                <TableRow key={arm.id}>
                  <TableCell sx={{ fontWeight: 500, color: 'text.dark' }}>
                    {arm.is_control ? 'Control' : armName(arm)}
                  </TableCell>
                  <TableCell>{orDash(arm.treatment_description)}</TableCell>
                  <TableCell>{orDash(labelsFor(CONTACT_MODE, arm.modes))}</TableCell>
                  {isOwner ? (
                    <>
                      <TableCell align="right">
                        {orDash(formatNumber(arm.match_count?.uploaded_n ?? arm.initial_n))}
                      </TableCell>
                      <TableCell align="right">{orDash(formatNumber(arm.match_count?.valid_ids))}</TableCell>
                      <TableCell align="right">{orDash(formatPercent(arm.match_count?.match_rate))}</TableCell>
                    </>
                  ) : (
                    <TableCell align="right">{orDash(formatNumber(arm.initial_n))}</TableCell>
                  )}
                  <TableCell>
                    {arm.treatment_start_date || arm.treatment_end_date
                      ? `${formatDate(arm.treatment_start_date)} – ${formatDate(arm.treatment_end_date)}`
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainerWithScrollShadow>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <SectionHeading>Outcomes and analysis</SectionHeading>
          <DetailTable
            rows={[
              ['Data exclusions', outcome.data_exclusions],
              ['Missing data', outcome.missing_data],
              ['Regression model', regressionModel],
              ['Covariates', outcome.covariates],
              ['Subgroup analyses', outcome.subgroup_analyses],
              ['Additional analyses', outcome.additional_analyses],
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionHeading>Wrap-up</SectionHeading>
          <DetailTable
            rows={[
              ['Additional notes', experiment.notes],
              [
                'Embargo date',
                join(formatDate(experiment.embargo_date), labelFor(EMBARGO_PERIOD, experiment.embargo_period)),
              ],
              ['Post-embargo access', labelFor(POST_EMBARGO_ACCESS, experiment.post_embargo_access)],
              ...(experiment.post_embargo_access === 'by_request' && experiment.viewer_is_owner
                ? [['Shared with', (experiment.shared_organization_names || []).join(', ')]]
                : []),
              [
                'Anonymous',
                isShared && experiment.anonymous !== null
                  ? experiment.anonymous
                    ? 'Yes, please share anonymously'
                    : "No, share my organization's name"
                  : '',
              ],
            ]}
          />
        </Grid>
      </Grid>

      {isOwner && (
        <>
      <SectionHeading>Files &amp; materials</SectionHeading>
      {downloadError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {downloadError}
        </Alert>
      )}
      <TableContainerWithScrollShadow>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>File</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {experiment.files.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>
                  No files uploaded.
                </TableCell>
              </TableRow>
            ) : (
              experiment.files.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>{file.original_filename}</TableCell>
                  <TableCell>{fileTypeLabel(file, experiment.treatments)}</TableCell>
                  <TableCell align="right">
                    {file.kind === FILE_KIND.DATA ? (
                      <Chip size="small" label="Stored" />
                    ) : (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleDownload(file.id)}
                        sx={{ textDecoration: 'underline', minWidth: 'auto' }}
                      >
                        Download
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainerWithScrollShadow>
        </>
      )}
    </>
  );
};

const RegistryExperimentDetailPage = () => {
  const { projectId, experimentId } = useParams();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organization');
  const navigate = useNavigate();
  const [experiment, setExperiment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Experiments shared with the viewing org are only reachable through that org's view.
  const load = useCallback(async () => {
    setError('');
    try {
      const query = organizationId ? `?organization=${organizationId}` : '';
      setExperiment(await fetchList(`/registry/my-experiments/${experimentId}${query}`));
    } catch (e) {
      setError('Could not load this experiment.');
    } finally {
      setLoading(false);
    }
  }, [experimentId, organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (
      experiment?.status === EXPERIMENT_STATUS.DRAFT ||
      experiment?.status === EXPERIMENT_STATUS.NEEDS_UPDATES
    ) {
      navigate(`/projects/${projectId}/registry/${experimentId}/edit/step1`, { replace: true });
    }
  }, [experiment, projectId, experimentId, navigate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !experiment) {
    return (
      <>
        <RegistryBreadcrumbs projectId={projectId} current="Experiment" />
        <Alert severity="error">{error || 'Experiment not found.'}</Alert>
      </>
    );
  }

  if (
    experiment.status === EXPERIMENT_STATUS.DRAFT ||
    experiment.status === EXPERIMENT_STATUS.NEEDS_UPDATES
  ) {
    return null;
  }

  const isRegistered = experiment.status === EXPERIMENT_STATUS.REGISTERED;
  const subtitle = join(
    experiment.registry_id,
    isRegistered
      ? `Registered ${formatDateTime(experiment.registered_at)}`
      : `Submitted ${formatDateTime(experiment.submitted_at)}`
  );

  const headerAction = (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {experiment.status === EXPERIMENT_STATUS.VALIDATING && (
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      )}
      <RegistryStatusChip status={experiment.status} />
    </Box>
  );

  return (
    <Box>
      <RegistryBreadcrumbs projectId={projectId} current={experiment.registry_id || 'Experiment'} />
      <PageHeader title={experiment.experiment_name || 'Untitled experiment'} subtitle={subtitle} action={headerAction} />
      <Box sx={{ mt: 3 }}>
        {experiment.status === EXPERIMENT_STATUS.VALIDATING && <ValidatingView experiment={experiment} />}
        {experiment.status === EXPERIMENT_STATUS.VERIFY_COUNTS && (
          <VerifyCountsView
            experiment={experiment}
            onChanged={(updated) => (updated ? setExperiment(updated) : load())}
          />
        )}
        {experiment.status === EXPERIMENT_STATUS.NEEDS_APPROVAL && (
          <NeedsApprovalView
            experiment={experiment}
            onChanged={(updated) => (updated ? setExperiment(updated) : load())}
          />
        )}
        {(isRegistered || experiment.status === EXPERIMENT_STATUS.NEEDS_APPROVAL) && (
          <RegisteredView experiment={experiment} />
        )}
      </Box>
    </Box>
  );
};

const routeArgs = {
  element: <RegistryExperimentDetailPage />,
};

export default buildRoute(routeArgs);
